import { useState } from 'react';
import { motion } from 'framer-motion';
import { Phone, Mail, Clock, MapPin, Check, Loader2, ArrowRight } from 'lucide-react';
import { toast } from 'sonner';

const CLEANING_TYPES = ['Residential', 'Commercial — Office', 'Commercial — Restaurant', 'Commercial — Retail'];
const SERVICE_TYPES = ['Regular Cleaning', 'Deep Cleaning', 'Post-Construction Cleaning', 'Move-In / Move-Out Cleaning'];
const FREQS = ['One-time', 'Weekly', 'Bi-weekly', 'Monthly'];
const CITIES = ['Surrey', 'Vancouver', 'Langley', 'Coquitlam', 'Richmond', 'Burnaby', 'Other'];

const inputCls = 'w-full rounded-xl border border-stone-300/80 bg-white px-4 py-3.5 text-sm text-obsidian placeholder:text-stone-400 outline-none transition focus:border-mint focus:ring-2 focus:ring-mint/20';

const INFO = [
  { icon: Phone, label: 'Call us', value: '+1 (647) 390-0855', href: 'tel:+16473900855' },
  { icon: Mail, label: 'Email', value: 'infogmpsolution@gmail.com', href: 'mailto:infogmpsolution@gmail.com' },
  { icon: Clock, label: 'Hours', value: '8:00 AM – 11:00 PM, 7 days a week', href: null },
  { icon: MapPin, label: 'Service area', value: 'Surrey · Vancouver · Langley · Coquitlam · Richmond · Burnaby & beyond', href: null },
];

export default function QuoteForm() {
  const [form, setForm] = useState({
    name: '', email: '', phone: '', cleaning_type: '', service: '', frequency: '', city: '', message: '',
  });
  const [sending, setSending] = useState(false);
  const [done, setDone] = useState(null);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const valid =
    form.name.trim() && /\S+@\S+\.\S+/.test(form.email) && form.phone.trim().length >= 7 &&
    form.cleaning_type && form.service && form.frequency && form.city;

  const submit = async (e) => {
    e.preventDefault();
    if (!valid) {
      toast.error('Please complete all required fields.');
      return;
    }
    setSending(true);
    try {
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/quotes`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(form),
      });
      if (!res.ok) throw new Error('fail');
      const data = await res.json();
      setDone(data.id || data._id);
      toast.success('Quote request received — we reply within 2 business hours.');
    } catch {
      toast.error('Could not submit right now. Please try again.');
    } finally {
      setSending(false);
    }
  };

  return (
    <section id="quote" data-testid="quote-section" className="bg-ceramic py-24 lg:py-36">
      <div className="max-w-7xl mx-auto px-5 sm:px-8">
        <div className="mb-14">
          <p className="font-mono text-xs uppercase tracking-[0.3em] text-mint mb-5">05 — Free Quote</p>
          <h2 className="font-display font-extrabold text-4xl sm:text-5xl lg:text-6xl tracking-tight text-obsidian leading-[1.05]">
            Get your free quote
            <br />
            in minutes.
          </h2>
        </div>

        <div className="grid lg:grid-cols-12 gap-10 lg:gap-16 items-start">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.15 }}
            transition={{ duration: 0.8 }}
            className="lg:col-span-5 lg:sticky lg:top-28 order-2 lg:order-1"
          >
            <div className="relative rounded-3xl bg-obsidian grain p-9 lg:p-11 overflow-hidden">
              <div className="absolute -top-20 -right-20 w-72 h-72 rounded-full bg-mint/20 blur-[90px]" />
              <p className="relative font-mono text-[11px] uppercase tracking-[0.25em] text-mint-vibrant">
                Prefer to talk?
              </p>
              <p className="relative mt-4 text-white/90 text-base lg:text-lg leading-relaxed">
                Skip the form — call or email and we'll build your quote together.
              </p>
              <div className="relative mt-8 space-y-5 border-t border-white/10 pt-8">
                {INFO.map((d) => (
                  <div key={d.label} data-testid={`quote-info-${d.label.toLowerCase().replace(/\s+/g, '-')}`} className="flex items-start gap-4">
                    <span className="w-10 h-10 rounded-xl bg-white/5 border border-white/15 flex items-center justify-center shrink-0">
                      <d.icon size={16} className="text-mint-vibrant" />
                    </span>
                    <div>
                      <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-stone-400">{d.label}</p>
                      {d.href ? (
                        <a href={d.href} className="text-white font-semibold text-sm mt-0.5 hover:text-mint-vibrant transition-colors">
                          {d.value}
                        </a>
                      ) : (
                        <p className="text-white font-semibold text-sm mt-0.5">{d.value}</p>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.15 }}
            transition={{ duration: 0.8, delay: 0.1 }}
            className="lg:col-span-7 order-1 lg:order-2"
          >
            {done ? (
              <div data-testid="quote-success" className="rounded-3xl bg-white border border-stone-200/80 p-10 lg:p-14 text-center">
                <div className="w-16 h-16 mx-auto rounded-full bg-mint flex items-center justify-center">
                  <Check size={30} className="text-white" strokeWidth={3} />
                </div>
                <h3 className="mt-6 font-display font-extrabold text-obsidian text-3xl tracking-tight">Request received.</h3>
                <p className="mt-3 text-stone-600">
                  Reference <span className="font-mono font-semibold text-obsidian">#{done.slice(-8).toUpperCase()}</span> —
                  we'll confirm your cleaning plan and schedule within 2 business hours.
                </p>
              </div>
            ) : (
              <form
                data-testid="quote-form"
                onSubmit={submit}
                className="rounded-3xl bg-white border border-stone-200/80 shadow-[0_20px_60px_rgba(42,32,26,0.06)] p-7 sm:p-10"
              >
                <div className="grid sm:grid-cols-2 gap-4">
                  <input data-testid="quote-input-name" placeholder="Full Name *" value={form.name} onChange={(e) => set('name', e.target.value)} className={inputCls} />
                  <input data-testid="quote-input-phone" type="tel" placeholder="Phone Number *" value={form.phone} onChange={(e) => set('phone', e.target.value)} className={inputCls} />
                  <input data-testid="quote-input-email" type="email" placeholder="Email Address *" value={form.email} onChange={(e) => set('email', e.target.value)} className={`${inputCls} sm:col-span-2`} />
                  <select data-testid="quote-select-cleaning-type" value={form.cleaning_type} onChange={(e) => set('cleaning_type', e.target.value)} className={`${inputCls} ${form.cleaning_type ? '' : 'text-stone-400'}`}>
                    <option value="">Cleaning for… *</option>
                    {CLEANING_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
                  </select>
                  <select data-testid="quote-select-service" value={form.service} onChange={(e) => set('service', e.target.value)} className={`${inputCls} ${form.service ? '' : 'text-stone-400'}`}>
                    <option value="">Service needed… *</option>
                    {SERVICE_TYPES.map((s) => <option key={s} value={s}>{s}</option>)}
                  </select>
                  <select data-testid="quote-select-city" value={form.city} onChange={(e) => set('city', e.target.value)} className={`${inputCls} ${form.city ? '' : 'text-stone-400'}`}>
                    <option value="">City… *</option>
                    {CITIES.map((c) => <option key={c} value={c}>{c}</option>)}
                  </select>
                </div>

                <div className="mt-7">
                  <p className="text-sm font-semibold text-obsidian mb-3">How often? *</p>
                  <div className="flex flex-wrap gap-2.5">
                    {FREQS.map((f) => (
                      <button
                        key={f}
                        type="button"
                        data-testid={`quote-frequency-${f.toLowerCase().replace(/\s+/g, '-')}`}
                        onClick={() => set('frequency', f)}
                        className={`px-5 py-2.5 rounded-full border-2 text-sm font-medium transition-all duration-300 ${
                          form.frequency === f
                            ? 'border-mint bg-mint text-white'
                            : 'border-stone-200 text-stone-600 hover:border-mint/50'
                        }`}
                      >
                        {f}
                      </button>
                    ))}
                  </div>
                </div>

                <textarea
                  data-testid="quote-input-message"
                  rows="4"
                  placeholder="Tell us about your space — size, rooms, priorities, access…"
                  value={form.message}
                  onChange={(e) => set('message', e.target.value)}
                  className={`${inputCls} mt-5 resize-none`}
                />

                <button
                  type="submit"
                  data-testid="quote-submit-button"
                  disabled={sending}
                  className="group mt-8 w-full sm:w-auto inline-flex items-center justify-center gap-2 bg-mint hover:bg-mint-deep text-white font-semibold text-sm px-9 py-4 rounded-full transition-all duration-300 hover:scale-[1.02] active:scale-95 disabled:opacity-60"
                >
                  {sending ? <Loader2 size={16} className="animate-spin" /> : null}
                  {sending ? 'Sending…' : 'Get My Free Quote'}
                  {!sending && <ArrowRight size={16} className="group-hover:translate-x-1 transition-transform" />}
                </button>
              </form>
            )}
          </motion.div>
        </div>
      </div>
    </section>
  );
}
